/**
 * 任務補登服務（LINE@ 司機端）
 * 對齊既有 LINE@ 單層 service pattern（taskService / dailyReportService 風格）：
 *   - 直接呼叫 apiClient
 *   - config.useMock 切換 mock / api
 *   - 所有 endpoint 走 /line/* 前綴（搭配 DriverTokenType / LineUserTokenType）
 *
 * 對應後端 LineMissionAmendmentController:
 *   GET  /api/line/drivers/me/mission-amendments
 *   POST /api/line/mission-amendments/one-shot
 *   PUT  /api/line/mission-amendments/{id}/complete
 *   GET  /api/line/daily-reports/by-driver
 *   GET  /api/line/task-q/by-container/{containerNo}
 */

import { config } from './config'
import apiClient from './apiClient'
import type {
  CompleteAssignedShellRequest,
  CreateOneShotAmendmentRequest,
  DailyReportListItem,
  MissionAmendment,
  TaskQItem,
} from '@/types/MissionAmendment'

class MissionAmendmentService {
  // ====================================================================
  // 列表（依司機，當前認證身分由後端 SecurityContext 取）
  // ====================================================================

  async listByDriver(_driverId: string, status?: string[]): Promise<MissionAmendment[]> {
    if (config.useMock) {
      console.log('[MissionAmendmentService] mock: listByDriver')
      return []
    }
    return apiClient.get<MissionAmendment[]>('/line/drivers/me/mission-amendments', {
      params: status && status.length > 0 ? { status } : undefined,
    })
  }

  // ====================================================================
  // 自發 / 從 TASK_Q 補登（SELF_LINEAT / FROM_TASK_Q）
  // ====================================================================

  async createOneShot(request: CreateOneShotAmendmentRequest): Promise<MissionAmendment> {
    if (config.useMock) {
      console.log('[MissionAmendmentService] mock: createOneShot')
      throw new Error('mock not implemented')
    }
    // 後端從 SecurityContext 取得 driverId，request 內 driverId 會被 controller 覆寫
    return apiClient.post<MissionAmendment>('/line/mission-amendments/one-shot', request)
  }

  // ====================================================================
  // 草稿 — 司機自存（PENDING_FILL，不送出）
  // ====================================================================

  /**
   * v2.3.x：草稿 payload 允許帶 dailyReportId / trailerNo
   * - 從過往打卡日期選 → 一併帶 dailyReportId + trailerNo + dutyDate
   * - 手選日期 → dailyReportId=null；trailerNo 從車輛下拉選
   * driverId 後端從 token 取，前端不傳
   */
  async createDraft(request: Omit<CreateOneShotAmendmentRequest, 'driverId'> & {
    mode: 'SELF_LINEAT' | 'FROM_TASK_Q'
    sourceTaskQId?: string | null
  }): Promise<MissionAmendment> {
    if (config.useMock) throw new Error('mock not implemented')
    return apiClient.post<MissionAmendment>('/line/mission-amendments/drafts', request)
  }

  async updateDraft(id: string, request: Omit<CreateOneShotAmendmentRequest, 'mode' | 'driverId' | 'sourceTaskQId'>): Promise<MissionAmendment> {
    if (config.useMock) throw new Error('mock not implemented')
    return apiClient.put<MissionAmendment>(`/line/mission-amendments/${id}`, request)
  }

  // v2.3：RETURNED → PENDING_REVIEW（搭配 updateDraft 一起，與「儲存」動作分離）
  async resubmit(id: string): Promise<MissionAmendment> {
    if (config.useMock) throw new Error('mock not implemented')
    return apiClient.post<MissionAmendment>(`/line/mission-amendments/${id}/resubmit`, {})
  }

  // ====================================================================
  // 完成被指派的殼（COMPLETE_ASSIGNED）
  // ====================================================================

  async completeAssignedShell(
    id: string,
    request: CompleteAssignedShellRequest,
  ): Promise<MissionAmendment> {
    if (config.useMock) {
      console.log('[MissionAmendmentService] mock: completeAssignedShell')
      throw new Error('mock not implemented')
    }
    return apiClient.put<MissionAmendment>(`/line/mission-amendments/${id}/complete`, request)
  }

  /** v2 #2.1：REJECTED → PENDING_FILL，司機可在退件後繼續修改並再次送出 */
  async reopen(id: string): Promise<MissionAmendment> {
    if (config.useMock) throw new Error('mock not implemented')
    return apiClient.post<MissionAmendment>(`/line/mission-amendments/${id}/reopen`, {})
  }

  /**
   * 驗證貨櫃號碼（ISO 6346 Check Digit）
   * @throws ApiBusinessError code=DSP-0706 格式錯（4 英文 + 7 數字）/ DSP-0707 檢核碼不符
   */
  async validateContainerNo(value: string): Promise<void> {
    await apiClient.get<void>('/line/container-no/validate', { params: { value } })
  }

  // ====================================================================
  // 打卡視窗（依司機，後端取認證身分）
  // ====================================================================

  async searchDailyReportsByDriver(params: {
    driverId: string  // 保留簽名，但 LINE@ endpoint 不需傳；後端自取
    fromDate?: string
    toDate?: string
    vehicleNo?: string
  }): Promise<DailyReportListItem[]> {
    if (config.useMock) {
      console.log('[MissionAmendmentService] mock: searchDailyReportsByDriver')
      return []
    }
    const { driverId: _ignore, ...query } = params
    return apiClient.get<DailyReportListItem[]>('/line/daily-reports/by-driver', { params: query })
  }

  // ====================================================================
  // TASK_Q 依貨櫃號查找（掃描入口）
  // ====================================================================

  async findTaskQByContainer(containerNo: string): Promise<TaskQItem[]> {
    if (config.useMock) {
      console.log('[MissionAmendmentService] mock: findTaskQByContainer')
      return []
    }
    return apiClient.get<TaskQItem[]>(
      `/line/task-q/by-container/${encodeURIComponent(containerNo)}`,
    )
  }

  // ====================================================================
  // 船掛 Lookup（取船名 + 船公司清單）
  // ====================================================================

  /**
   * v2.3：委託人候選清單（對齊 web 三來源聯集）
   * - supplierCompany 為司機所屬拖車公司代碼；空值僅回 company 共享來源
   * - 來源：customer (type 20/30/40, member_cd=supplier) + company (type 10/50 跨會員)
   */
  async listConsignorOptions(supplierCompany?: string | null): Promise<Array<{
    code: string
    name: string
    source: 'CUSTOMER' | 'COMPANY'
    typeOrGroup: string
  }>> {
    if (config.useMock) {
      console.log('[MissionAmendmentService] mock: listConsignorOptions')
      return []
    }
    return apiClient.get('/line/mission-amendments/consignor-options', {
      params: supplierCompany ? { supplierCompany } : undefined,
    })
  }

  async lookupVessel(vslRegNo: string): Promise<{
    vslRegNo: string
    vslName: string
    workStatus: string
    shipCompanies?: Array<{ value: string; label: string }>
  } | null> {
    if (config.useMock) {
      console.log('[MissionAmendmentService] mock: lookupVessel')
      return null
    }
    try {
      return await apiClient.get<{
        vslRegNo: string
        vslName: string
        workStatus: string
        shipCompanies?: Array<{ value: string; label: string }>
      }>(`/line/vessels/lookup/${encodeURIComponent(vslRegNo)}`)
    } catch {
      return null
    }
  }

  // ====================================================================
  // 縣市別 / 地點 dropdown（cascade location field 用）
  // ====================================================================

  async listCityAreaCodes(): Promise<Array<{ code: string; name: string }>> {
    if (config.useMock) return []
    try {
      return await apiClient.get<Array<{ code: string; name: string }>>('/line/codes/city-areas')
    } catch {
      return []
    }
  }

  async listLocationsByArea(areaCode: string): Promise<Array<{
    value: string
    label: string
    shortName?: string
    areaCode?: string
    storageLocationTypes?: string[]
  }>> {
    if (config.useMock) return []
    if (!areaCode) return []
    try {
      return await apiClient.get<Array<{
        value: string
        label: string
        shortName?: string
        areaCode?: string
        storageLocationTypes?: string[]
      }>>(`/line/locations/dropdown?areaCode=${encodeURIComponent(areaCode)}`)
    } catch {
      return []
    }
  }
}

export const missionAmendmentService = new MissionAmendmentService()
export default missionAmendmentService
